import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { EditStatusType, StateHelperService } from '@Core/services/proprietary/state-helper.service';
import { cloneDeep } from 'src/ep-utils';
import { ContractObjectDefaults } from './contract-object.defaults';
import {
    AddSubLocationLink,
    RemoveAllSubLocationLinks,
    RemoveSubLocationLink,
    UpdateLocation
} from './location.actions';
import { LocationState } from './location.state';
import {
    AddSubLocation,
    CloneSubLocation,
    DeleteSubLocation,
    OnLocationDeleted,
    RestoreSubLocation,
    SetCurrentSubLocation,
    SetCurrentSubLocationParent,
    UpdateSubLocation
} from './sub-location.actions';

export interface SubLocationStateModel {
    subLocation: any;
    currentID: string;
    parentID: any;
}

@State<SubLocationStateModel>({
    name: 'subLocationState',
    defaults: {
        subLocation: {},
        currentID: null,
        parentID: null
    }
})
@Injectable()
export class SubLocationState {
    constructor(private store: Store, private helper: StateHelperService) { }

    //******* SELECTORS *********

    @Selector()
    static subLocationCodes(state: SubLocationStateModel) {
        return (locId: any) => {
            let result: any = [{ Code: '', Code_Desc: '' }]; //include empty as option for clearing.
            if (locId) {
                for (var i in state.subLocation) {
                    let subLoc = state.subLocation[i];
                    if (subLoc.Edit_Status && subLoc.Edit_Status == EditStatusType.Delete) {
                        //don't include deleted sub locations in code table list
                        continue;
                    }
                    if (subLoc.Loc_ID == locId) {
                        result.push({
                            Code: subLoc.Sub_Loc_Num,
                            Code_Desc: 'SubLoc# ' + subLoc.Sub_Loc_Num + ': ' + subLoc.Sub_Loc_Desc
                        });
                    }
                }
            }

            return result;
        };
    }

    @Selector()
    static subLocationIDCodes(state: SubLocationStateModel) {
        return (locId: any) => {
            let result: any = [{ Code: '', Code_Desc: '' }]; //include empty as option for clearing.
            if (locId) {
                for (var i in state.subLocation) {
                    let subLoc = state.subLocation[i];
                    if (subLoc.Edit_Status && subLoc.Edit_Status == EditStatusType.Delete) {
                        //don't include deleted sub locations in code table list
                        continue;
                    }
                    if (subLoc.Loc_ID == locId) {
                        result.push({
                            Code: subLoc.Sub_Loc_ID,
                            Code_Desc: 'SubLoc# ' + subLoc.Sub_Loc_Num + ': ' + subLoc.Sub_Loc_Desc
                        });
                    }
                }
            }

            return result;
        };
    }

    @Selector()
    static currentSubLocation(state: SubLocationStateModel) {
        if (state.currentID) {
            return state.subLocation[state.currentID];
        } else {
            return {};
        }
    }

    @Selector()
    static subLocations(state: SubLocationStateModel) {
        //return all subLocations
        let result: any = [];
        for (var i in state.subLocation) {
            result.push(state.subLocation[i]);
        }
        return result;
    }

    @Selector()
    static subLocationBySubLocID(state: SubLocationStateModel) {
        return (subLocID: string) => {
            return state.subLocation[subLocID] || {};
        };
    }

    // @Selector()
    // static subLocationsByIDs(state: SubLocationStateModel) {
    //   //return subLocations based on an array of sub_Loc_ID passed in
    //   return (subLocIDs: any[]) => {
    //     let result: any = [];
    //     if (subLocIDs) {
    //       subLocIDs.forEach(function(thisID) {
    //         result.push(state.subLocation[thisID]);
    //       });
    //     }
    //     return result;
    //   };
    // }

    // @Selector()
    // static subLocationsByIDs(state: SubLocationStateModel) {
    //   //return subLocations based on an array of sub_Loc_ID passed in
    //   return (subLocIDs: any[]) => {
    //     let result: any = [];
    //     if (subLocIDs) {
    //       for (var i = 0; i < subLocIDs.length; i++) {
    //         let subLocID = subLocIDs[i];
    //         result.push(state.subLocation[subLocID]);
    //       }
    //     }
    //     return result;
    //   };
    // }

    @Selector()
    static subLocationCountByLocID(state: SubLocationStateModel) {
        //return sub location count for a particular location
        return (locID: any) => {
            let count = 0;
            for (var i in state.subLocation) {
                if (state.subLocation[i].Loc_ID == locID && state.subLocation[i].Edit_Status !== EditStatusType.Delete) {
                    count++;
                }
            }
            return count;
        };
    }

    @Selector()
    static subLocationsByLocID(state: SubLocationStateModel) {
        //return all sub locations for a particular location based on Loc ID
        return (locID: any) => {
            let subLocations: any[] = [];
            for (var i in state.subLocation) {
                if (state.subLocation[i].Loc_ID == locID) {
                    subLocations.push(state.subLocation[i]);
                }
            }
            return subLocations;
        };
    }

    @Selector()
    static subLocationParentID(state: SubLocationStateModel) {
        if (state.parentID) {
            return state.parentID;
        } else {
            return '';
        }
    }

    @Selector()
    static currentSubLocID(state: SubLocationStateModel) {
        if (state.currentID) {
            return state.currentID;
        } else {
            return null;
        }
    }

    //******  ACTIONS  ******
    @Action(SetCurrentSubLocationParent)
    setCurrentSubLocationParent(sc: StateContext<SubLocationStateModel>, action: SetCurrentSubLocationParent) {
        const state = sc.getState();
        const locations = this.store.selectSnapshot<any>(LocationState.locations);
        let currentParentID = state.parentID;
        let locID = null;
        if (action.locNum) {
            for (var i in locations) {
                if (locations[i].Loc_Num == action.locNum) {
                    locID = locations[i].Loc_ID;
                    break;
                }
            }
        }
        if (locID && locID !== currentParentID) {
            sc.patchState({ parentID: locID });
        }
    }

    @Action(SetCurrentSubLocation)
    setCurrentSubLocation(sc: StateContext<SubLocationStateModel>, action: SetCurrentSubLocation) {
        const state = sc.getState();
        let subLocID = null;
        let locID = action.subLocation.Loc_ID;
        let subLocNum = action.subLocation.Sub_Loc_Num;
        if (locID && subLocNum) {
            for (var i in state.subLocation) {
                if (state.subLocation[i].Loc_ID == locID && state.subLocation[i].Sub_Loc_Num == subLocNum) {
                    subLocID = state.subLocation[i].Sub_Loc_ID;
                    break;
                }
            }
        }

        sc.patchState({ currentID: subLocID });
    }

    @Action(UpdateSubLocation)
    updateSubLocation(sc: StateContext<SubLocationStateModel>, action: UpdateSubLocation) {
        const state = sc.getState();
        const sublocations = state.subLocation;
        let locID = action.locID ? action.locID : action.subLocation.Loc_ID;
        if (!locID) {
            if (state.parentID) {
                locID = state.parentID;
            }
        }
        let subLocID = action.subLocation.Sub_Loc_ID;
        let newSubLocNum = action.subLocation.Sub_Loc_Num;
        let oldSubLocNum = state.subLocation[subLocID].Sub_Loc_Num;
        let updateSubLocation: any = { ...action.subLocation };
        updateSubLocation.Loc_ID = locID;
        this.helper.setItemChanged(updateSubLocation, state.subLocation[subLocID].Edit_Status);

        if (newSubLocNum && newSubLocNum != '' && oldSubLocNum && oldSubLocNum != '' && newSubLocNum != oldSubLocNum) {
            let currentSubLocations: any = [];
            for (var i in sublocations) {
                currentSubLocations.push(sublocations[i]);
            }

            // let idx = currentSubLocations.map((x) => x.Sub_Loc_Num).indexOf(newSubLocNum);
            let idx = currentSubLocations.findIndex((subLoc) => subLoc.Loc_ID == locID && subLoc.Sub_Loc_Num == newSubLocNum);

            if (idx > -1) {
                // Not unique Sub_Loc_Num, reject the change
                updateSubLocation.Sub_Loc_Num = oldSubLocNum;
            }
        }

        sc.patchState({
            subLocation: {
                ...state.subLocation,
                [subLocID]: {
                    ...state.subLocation[subLocID],
                    ...updateSubLocation
                }
            }
        });

        const locations = this.store.selectSnapshot<any>(LocationState.locations);
        let updateLocation: any = {};
        for (var i in locations) {
            if (locations[i].Loc_ID == locID) {
                updateLocation = locations[i];
                break;
            }
        }
        this.store.dispatch(new UpdateLocation(updateLocation));
    }

    @Action(DeleteSubLocation)
    deleteSubLocation(sc: StateContext<SubLocationStateModel>, action: DeleteSubLocation) {
        const state = sc.getState();
        let locID = action.locID ? action.locID : state.subLocation[action.subLocID].Loc_ID;
        let subLocNum = state.subLocation[action.subLocID].Sub_Loc_Num;
        let subLocation = { ...state.subLocation };
        this.helper.setItemDeleted(subLocation, action.subLocID);
        sc.patchState({ subLocation: subLocation });
        this.store.dispatch(new RemoveSubLocationLink(locID, action.subLocID, subLocNum));
    }

    @Action(OnLocationDeleted)
    onLocationDeleted(sc: StateContext<SubLocationStateModel>, action: OnLocationDeleted) {
        //when a location gets deleted, delete all of the sub locations first
        const state = sc.getState();
        let subLocID = null;
        let subLocIDs: any[] = [];
        for (var i in state.subLocation) {
            if (state.subLocation[i].Loc_ID == action.locID) {
                subLocIDs.push(state.subLocation[i].Sub_Loc_ID);
            }
        }
        for (var j in subLocIDs) {
            let subLocation = { ...state.subLocation };
            this.helper.setItemDeleted(subLocation, subLocIDs[j]);
            sc.patchState({ subLocation: subLocation });
        }
        return sc.dispatch(new RemoveAllSubLocationLinks(action.locID));
    }

    @Action(AddSubLocation)
    addSubLocation(sc: StateContext<SubLocationStateModel>, action: AddSubLocation) {
        const state = sc.getState();
        let locID = action.locID ? action.locID : action.subLocation.Loc_ID;
        let newSubLocID: any = this.helper.getTempID();
        let newSubLocation = this.helper.createDefaultItem(ContractObjectDefaults, 'Sub_Location', action.subLocation);
        newSubLocation.Loc_ID = locID;

        // ML - filter sub locations to the particular parent (location) in order to set correct sub loc num
        let subLocsForParent: any = {};
        for (var i in state.subLocation) {
            if (state.subLocation[i].Loc_ID == locID) {
                let subLocId: any = state.subLocation[i].Sub_Loc_ID;
                let subLoc: any = { ...state.subLocation[i] };
                subLocsForParent = { [subLocId]: subLoc };
            }
        }
        let newSubLocNum = this.helper.getNextItemNumber(subLocsForParent, 'Sub_Loc_Num').toString();

        this.helper.setItemAdded(newSubLocation);
        newSubLocation.Sub_Loc_ID = newSubLocID;
        newSubLocation.Sub_Loc_Num = newSubLocNum;

        sc.patchState({
            currentID: newSubLocID,
            subLocation: { ...state.subLocation, [newSubLocID]: newSubLocation }
        });

        this.store.dispatch(new AddSubLocationLink(locID, parseInt(newSubLocID, 10)));
    }

    @Action(CloneSubLocation)
    cloneSubLocation(sc: StateContext<SubLocationStateModel>, action: CloneSubLocation) {
        const state = sc.getState();
        let srcID = null;

        if (action.subLocID) {
            srcID = action.subLocID;
        } else {
            throw 'SubLocID not provided for Sub Location clone.';
        }

        let newID = this.helper.getTempID();
        let newSubLoc = cloneDeep(state.subLocation[srcID]);
        newSubLoc.Loc_ID = action.locID;
        // ML - filter sub locations to the particular parent (location) in order to set correct sub loc num
        let subLocsForParent: any = {};
        for (var i in state.subLocation) {
            if (state.subLocation[i].Loc_ID == action.locID) {
                let subLocId: any = state.subLocation[i].Sub_Loc_ID;
                let subLoc: any = { ...state.subLocation[i] };
                subLocsForParent = { [subLocId]: subLoc };
            }
        }
        let newSubLocNum = this.helper.getNextItemNumber(subLocsForParent, 'Sub_Loc_Num').toString();

        this.helper.setItemAdded(newSubLoc);
        newSubLoc.Sub_Loc_ID = newID;
        newSubLoc.Sub_Loc_Num = newSubLocNum;

        sc.patchState({
            currentID: newID,
            subLocation: { ...state.subLocation, [newID]: newSubLoc }
        });

        sc.dispatch(new AddSubLocationLink(action.locID, parseInt(newID, 10)));
    }

    @Action(RestoreSubLocation)
    restoreSubLocation(sc: StateContext<SubLocationStateModel>, action: RestoreSubLocation) {
        const state = sc.getState();
        let subLocation = { ...state.subLocation };
        let locID = subLocation[action.subLocID].Loc_ID;
        this.helper.setItemRestored(subLocation, action.subLocID);
        sc.patchState({ subLocation: subLocation });

        sc.dispatch(new UpdateLocation({ Loc_ID: locID }));
    }
}
